import {
  Flex,
  KdsCardFile,
  KdsDropfile,
  KdsText,
  KdsTextArea,
} from "@kazaar-platform/design-system";
import { useTranslate } from "@tolgee/react";
import { useContext, useState } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { createSearchParams, useNavigate } from "react-router-dom";

import { CollapseSection } from "@/components/CollapseSection/CollapseSection";
import { FileContext } from "@/components/FileUploadAsync/FileUploadAsync.context";
import { SIZE_5G } from "@/constants/fileSize.constants";
import { getDropfileErrorMessage } from "@/helpers/forms";
import { useDirectUpload } from "@/hooks/useDirectUpload";

import { type OrderItemConfigurationSchema } from "../OrderItemConfigForm.schema";
import { type BriefSectionProps } from "./BriefSection.types";

export const BriefSection = ({
  isOpen,
  onOpenChange,
  orderItem,
  isModificationRequest,
}: BriefSectionProps) => {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const fileContext = useContext(FileContext);
  const { uploadFile, createFileUploadUrl } = useDirectUpload();

  const [isUploadingFile, setIsUploadingFile] = useState(false);

  const methods = useFormContext<OrderItemConfigurationSchema>();

  const {
    fields: fieldsPreMediaFiles,
    remove: removePreMediaFiles,
    append,
  } = useFieldArray({
    name: "preMediaFiles",
    rules: {
      required: true,
    },
  });

  const preMediaFiles = methods.watch("preMediaFiles");
  const configIds = methods.watch("configIds");
  const specificRequest = methods.watch("specificRequest");
  const withPreMedia = methods.watch("withPreMedia");

  const handleFileUpload = async (file: File, index: number) => {
    setIsUploadingFile(true);

    const uploadDetails = await createFileUploadUrl(
      "sandboxItem.preMedia",
      file
    );

    const newSignedId = uploadDetails?.fileSignedId;

    fileContext?.pushFile(newSignedId, {
      fileName: file.name,
      size: file.size,
      contentType: file.type,
    });

    await uploadFile(file, uploadDetails);

    fileContext?.setFileUploadState(newSignedId, "uploaded");

    setIsUploadingFile(false);

    methods.setValue(
      `preMediaFiles.${index}`,
      {
        fileSignedId: newSignedId,
        isDeleted: false,
        fileName: file.name,
        size: file.size,
      },
      {
        shouldDirty: true,
        shouldValidate: true,
      }
    );
  };

  const handleEditBrief = () => {
    const queryParams = createSearchParams({
      orderId: orderItem.orderId,
      orderItemId: orderItem.id,
    });

    if (isModificationRequest) {
      queryParams.append("isModificationRequest", "true");
    }

    const catalogMediumId =
      orderItem.latestCatalogMediumVersionId ||
      orderItem.sandboxItem.catalogMediumId;

    void navigate(`/catalog/${catalogMediumId}?${queryParams}`);
  };

  return (
    <CollapseSection
      size="medium"
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      variant="transparent"
      title={t("orderItemConfig.section.briefTitle")}
      link={t("orderItemConfig.section.briefLink")}
      linkAction={handleEditBrief}
    >
      {/* Pre-media */}
      {!!withPreMedia && !isModificationRequest && (
        <div className="mb-sp-md flex flex-col gap-sp-md self-stretch rounded-br-sm bg-bg-disabled p-sp-md">
          <div>
            <KdsText size="subtitle-sm">
              {t("orderItemConfig.premedia.title")}
            </KdsText>
            <KdsText>{t("orderItemConfig.premedia.subtitle")}</KdsText>
          </div>

          <div>
            <KdsText color="text-bodyvariant">
              {t("orderItemConfig.premedia.commentLabel")}
            </KdsText>
            <KdsTextArea
              placeholder={t("orderItemConfig.premedia.commentPlaceholder")}
              hasError={!!methods.formState.errors.preMediaBrief}
              {...methods.register("preMediaBrief")}
            />
          </div>

          <div className="flex flex-col">
            <KdsText color="text-bodyvariant">
              {t("orderItemConfig.premedia.fileLabel")}
            </KdsText>
            <div className="flex flex-col gap-sp-xs">
              {fieldsPreMediaFiles.map((preMediaFile, index) => {
                if (
                  (!!preMediaFiles?.[index]?.blobId ||
                    !!preMediaFiles?.[index]?.fileName) &&
                  !!preMediaFiles?.[index]?.isDeleted
                ) {
                  return null;
                }

                if (
                  (!!preMediaFiles?.[index]?.blobId ||
                    !!preMediaFiles?.[index]?.fileName) &&
                  !preMediaFiles?.[index]?.isDeleted
                ) {
                  return (
                    <KdsCardFile
                      key={preMediaFile.id}
                      fileName={preMediaFiles?.[index]?.fileName}
                      fileUrl={preMediaFiles?.[index]?.url}
                      fileSize={preMediaFiles?.[index]?.size}
                      isDeletable
                      isViewable
                      onDelete={() => {
                        if (preMediaFiles?.[index]?.blobId) {
                          methods.setValue(
                            `preMediaFiles.${index}.isDeleted`,
                            true,
                            {
                              shouldDirty: true,
                              shouldValidate: true,
                            }
                          );
                        } else {
                          removePreMediaFiles(index);
                        }
                      }}
                    />
                  );
                }

                return (
                  <Controller
                    key={preMediaFile.id}
                    control={methods.control}
                    name={`preMediaFiles.${index}.fileSignedId`}
                    render={({ field, fieldState }) => (
                      <KdsDropfile
                        errorMessage={getDropfileErrorMessage(
                          fieldState.error?.message
                        )}
                        hasError={!!fieldState.error}
                        isDeletable={!!field.value}
                        isLoading={isUploadingFile}
                        isReadable={!!field.value}
                        maxSize={SIZE_5G}
                        onDelete={() => removePreMediaFiles(index)}
                        onUploadError={() =>
                          methods.setValue(
                            `preMediaFiles.${index}.fileSignedId`,
                            undefined
                          )
                        }
                        onUploadStart={() => {
                          methods.clearErrors(
                            `preMediaFiles.${index}.fileSignedId`
                          );
                        }}
                        onUpload={(file) => void handleFileUpload(file, index)}
                        onUploadSuccess={() => {
                          append({
                            fileSignedId: "",
                            isDeleted: false,
                            blobId: "",
                            fileName: "",
                            size: undefined,
                            url: "",
                          });
                        }}
                        placeholderDescription={t(
                          "orderItemConfig.premedia.filePlaceholderDescription"
                        )}
                        placeholderTitle={t(
                          "orderItemConfig.premedia.filePlaceholderTitle"
                        )}
                      />
                    )}
                  />
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Configuration */}
      {!!configIds?.length &&
        configIds?.map((configId) => {
          const configurator =
            orderItem.sandboxItem.catalogMedium?.Configurators.find(
              (configurator) =>
                configurator.options.some((opt) => opt.id === configId)
            );
          const configuratorOption = configurator?.options.find(
            (opt) => opt.id === configId
          )?.name;

          if (!configurator || !configuratorOption) {
            return null;
          }

          return (
            <Flex key={configId} gap="sp-xs" align="center" wrap="wrap">
              <KdsText color="text-bodyvariant">{`${configurator.name} :`}</KdsText>
              <KdsText>{configuratorOption}</KdsText>
            </Flex>
          );
        })}

      {/* Specific request */}
      <Flex
        direction={specificRequest ? "column" : "row"}
        gap="sp-xs"
        align={specificRequest ? "flex-start" : "center"}
      >
        <KdsText color="text-bodyvariant">
          {t("orderItemConfig.comment")}
        </KdsText>
        <KdsText>{specificRequest ? specificRequest : "-"}</KdsText>
      </Flex>
    </CollapseSection>
  );
};
